import { PUSH_API_BASE } from '../api/ZionServer.ts'
import { ensureSocketAuthKeyPair, socketAuthSignRaw } from '../crypto/socketAuth.ts'
import { getSessionToken } from '../storage/sessionStore.ts'

export type PushPayload = {
  title?: string
  body?: string
  url?: string
  sound?: string
  badge?: number
}

/**
 * Payload APNs/Web Push: titolo = nome mittente, body = testo messaggio, suono nativo iOS.
 */
export function buildNativeApnsPayload(senderName: string, messageText: string): PushPayload {
  const title = String(senderName ?? '').trim() || 'ZION'
  let body = String(messageText ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!body) body = 'Nuovo messaggio cifrato'
  if (body.length > 140) body = `${body.slice(0, 140)}…`
  return {
    title,
    body,
    sound: 'default',
    badge: 1,
  }
}

/**
 * Avvio Capacitor iOS/Android: listeners prima, poi permessi + register APNs.
 * Mai Notification.requestPermission() WebKit. Nessuna UI manuale.
 */
export async function ensureNativePushOnLaunch(): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) return
    const { initNativePushHooks } = await import('./nativePush.ts')
    await initNativePushHooks()
  } catch (err) {
    console.error('Auto push registration error:', err)
  }
}

/** Title/body/sound sempre valorizzati — iOS scarta banner/suoni incompleti. */
export function normalizePushPayload(payload: PushPayload = {}): {
  title: string
  body: string
  url: string
  sound: string
  badge: number
} {
  const badgeRaw = Number(payload.badge)
  return {
    title: String(payload.title ?? '').trim() || 'ZION',
    body: String(payload.body ?? '').trim() || 'Nuovo messaggio cifrato',
    url: String(payload.url ?? '').trim() || 'https://www.zionyx.ch/app/',
    sound: String(payload.sound ?? '').trim() || 'default',
    badge: Number.isFinite(badgeRaw) && badgeRaw >= 0 ? Math.floor(badgeRaw) : 1,
  }
}

async function readVapidResponse(res: Response): Promise<string | null> {
  const ct = res.headers.get('content-type') ?? ''
  const raw = await res.text()
  if (!res.ok) return null
  if (!ct.includes('json') && raw.trimStart().startsWith('<')) {
    return null
  }
  try {
    const data = JSON.parse(raw) as { publicKey?: string }
    return data.publicKey?.trim() || null
  } catch {
    return null
  }
}

export async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const res = await fetch(`${PUSH_API_BASE}/zion/push/vapid-public-key`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
    return readVapidResponse(res)
  } catch {
    return null
  }
}

export async function registerPushOnServer(
  sessionToken: string,
  subscription: PushSubscription,
): Promise<boolean> {
  const res = await fetch(`${PUSH_API_BASE}/zion/push/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionToken,
      subscription: subscription.toJSON(),
    }),
  })
  return res.ok
}

export async function sendPushDirect(
  subscription: PushSubscriptionJSON,
  payload: PushPayload,
): Promise<boolean> {
  const normalized = normalizePushPayload(payload)
  const res = await fetch(`${PUSH_API_BASE}/zion/push/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subscription,
      title: normalized.title,
      body: normalized.body,
      url: normalized.url,
      click_action: normalized.url,
      sound: 'default',
      badge: normalized.badge,
    }),
  })
  return res.ok
}

/**
 * Sveglia il peer (Web Push / APNs bridge).
 * Sempre sound: 'default' + badge ≥ 1 per banner/suono iOS nativo.
 */
export async function wakePeerBySession(
  toSessionToken: string,
  payload: PushPayload,
): Promise<boolean> {
  const sess = toSessionToken.trim()
  if (!sess) return false
  const fromSession = (await getSessionToken())?.trim() || ''
  if (!fromSession) return false
  const normalized = normalizePushPayload(payload)
  try {
    const { privateKey } = await ensureSocketAuthKeyPair()
    const ts = String(Date.now())
    const signPayload = `${fromSession}:${sess}:push_wake:${ts}`
    const sig = await socketAuthSignRaw(privateKey, signPayload)
    const res = await fetch(`${PUSH_API_BASE}/zion/push/wake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        toSessionToken: sess,
        fromSessionToken: fromSession,
        ts,
        sig,
        title: normalized.title,
        body: normalized.body,
        url: normalized.url,
        sound: 'default',
        badge: normalized.badge,
      }),
      keepalive: true,
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Badge icona app nativa.
 * Nota: `@capacitor/push-notifications` NON espone `setBadge` in Cap 8.
 * API compatibile: `setBadge({ count })` → `@capawesome/capacitor-badge`.
 */
export async function setBadge(opts: { count: number }): Promise<void> {
  const count = Math.max(0, Math.floor(Number(opts.count) || 0))
  try {
    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) {
      if ('setAppBadge' in Navigator.prototype) {
        if (count > 0) await navigator.setAppBadge(count)
        else await navigator.clearAppBadge()
      }
      return
    }
    const { Badge } = await import('@capawesome/capacitor-badge')
    await Badge.set({ count })
  } catch (err) {
    console.warn('[ZION] setBadge failed', err)
  }
}

/** Alias esplicito richiesto dal fix PushNotifications.setBadge({ count }). */
export async function setPushNotificationsBadge(count: number): Promise<void> {
  await setBadge({ count })
}

/**
 * Dice al server se la PWA è in foreground → skip banner Web Push.
 * Su Capacitor nativo NON usare: serve APNs anche ad app aperta (banner di sistema).
 */
export async function reportPushPresence(
  sessionToken: string,
  visible: boolean,
): Promise<boolean> {
  const sess = sessionToken.trim()
  if (!sess) return false
  try {
    const res = await fetch(`${PUSH_API_BASE}/zion/push/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: sess, visible }),
      keepalive: true,
    })
    return res.ok
  } catch {
    return false
  }
}
